import React, { useCallback, useMemo } from 'react'

import ObjectDataGrid from 'features/admin/datagrid/ObjectDataGrid'
import useSlidingPane from 'features/workspace/AdminSideTray/hooks/useSlidingPane'

import { Avatar } from 'ui/components/Avatar'
import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { DropdownHeadSearch, DropdownItem } from 'ui/components/Dropdown'
import { Icon } from 'ui/components/Icon'
import { Spinner } from 'ui/components/Spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'ui/components/Tabs'
import { Body, Headline } from 'ui/components/Text'
import { stopPropagation } from 'ui/helpers/utilities'

import { useManageDataTablesState } from './hooks/useManageDataTablesState'
import { useObjectTabTriggerState } from './tabs/useObjectTabTriggerState'
import { ManageDataAddOrImportDropdown } from './ManagaDataAddOrImportDropdown'
import { ManageDataTabDropdown } from './ManageDataTabDropdown'
import { ManageDataTabRenamePopup } from './ManageDataTabRenamePopup'

import { ManageDataTablesTabsListStyle, ManageDataTablesTabsStyle } from './ManageDataTables.css'

type ManageDataTablesProps = {
    objects: ObjectDto[]
    dataConnections: DataConnectionDto[]
    onSelectObject: (objectSid: string) => void
    selectedObjectIdx?: number
    isLoading?: boolean
    isDirtyPanes?: Record<string, boolean>
    updateDirtyPanes: (objectSid: string, dirty: boolean) => void
    onOpenImportCsvModal: () => void
    onOpenImportTableDataModal: () => void
}

export const ManageDataTables: React.FC<ManageDataTablesProps> = ({
    objects,
    dataConnections,
    selectedObjectIdx = 0,
    onSelectObject,
    isLoading = false,
    isDirtyPanes,
    updateDirtyPanes,
    onOpenImportCsvModal,
    onOpenImportTableDataModal,
}) => {
    const {
        onOpenImportModal,
        selectedObjectSid,
        onOpenPermissionsModal,
        onOpenSettingsModal,
        toolbarModals,
    } = useManageDataTablesState({
        objects,
        selectedObjectIdx,
    })

    return (
        <Box height="full" width="full" background="surface">
            <Tabs
                width="full"
                height="full"
                value={selectedObjectSid}
                onValueChange={onSelectObject}
                className={ManageDataTablesTabsStyle}
            >
                <Box minWidth={0} width="full">
                    <ManageDataHeader />
                    <ObjectTabList
                        isLoading={isLoading}
                        objects={objects}
                        dataConnections={dataConnections}
                        isDirtyPanes={isDirtyPanes}
                        onSelectObject={onSelectObject}
                        onOpenImportModal={onOpenImportModal}
                        onOpenPermissionsModal={onOpenPermissionsModal}
                        onOpenSettingsModal={onOpenSettingsModal}
                        onOpenImportCsvModal={onOpenImportCsvModal}
                        onOpenImportTableDataModal={onOpenImportTableDataModal}
                    />
                </Box>
                <ObjectTabContent
                    isLoading={isLoading}
                    objects={objects}
                    selectedObjectSid={selectedObjectSid}
                    updateDirtyPanes={updateDirtyPanes}
                />
                <LoadingScreen isLoading={isLoading} />
            </Tabs>
            {toolbarModals}
        </Box>
    )
}

type ManageDataHeaderProps = React.ComponentPropsWithoutRef<typeof Box>

const ManageDataHeader: React.FC<ManageDataHeaderProps> = React.memo(
    function ManageDataHeader(props) {
        const { close } = useSlidingPane()

        return (
            <Box px="4xl" flex center gap="m" pt="l" pb="m" {...props}>
                <Avatar size="xs" type="icon" icon={{ name: 'Database' }} shape="square" noShrink />
                <Box noShrink>
                    <Headline size="xs">Data tables</Headline>
                </Box>
                <Box shrink trim color="textWeaker" fontSize="bodyS">
                    <Body size="s" color="textWeaker" display="block" style={{ lineHeight: 1 }}>
                        Manage the tables and data which power your app.
                    </Body>
                </Box>
                <Box style={{ marginLeft: 'auto' }}>
                    <Button
                        onClick={() => close()}
                        startIcon={{ name: 'X' }}
                        aria-label="Close"
                        size="s"
                        style={{
                            padding: 0,
                            width: 'auto',
                            height: 'auto',
                        }}
                        variant="ghost"
                    />
                </Box>
            </Box>
        )
    }
)

type ManageDataGridProps = {
    objectSid: string
    isActive: boolean
    updateDirtyPanes: (objectSid: string, dirty: boolean) => void
}

const ManageDataGrid: React.FC<ManageDataGridProps> = ({
    objectSid,
    isActive,
    updateDirtyPanes,
}) => {
    const setIsDirty = useCallback(
        (dirty: boolean) => updateDirtyPanes(objectSid, dirty),
        [updateDirtyPanes, objectSid]
    )

    if (!isActive) return null

    return (
        <ObjectDataGrid
            hideLabel={true}
            objectId={objectSid}
            setIsDirty={setIsDirty}
            isActive={isActive}
        />
    )
}

type ObjectTriggerProps = {
    object: ObjectDto
    dataConnection?: DataConnectionDto
    isDirty?: boolean
    onSelectObject: (objectSid: string) => void
    onOpenImportModal: () => void
    onOpenPermissionsModal: () => void
    onOpenSettingsModal: () => void
}

type ObjectTabTriggerProps = React.ComponentPropsWithoutRef<typeof TabsTrigger> & ObjectTriggerProps

const ObjectTabTrigger = React.forwardRef<HTMLButtonElement, ObjectTabTriggerProps>(
    (
        {
            dataConnection,
            isDirty,
            object,
            onSelectObject,
            onOpenImportModal,
            onOpenPermissionsModal,
            onOpenSettingsModal,
            ...props
        },
        ref
    ) => {
        const {
            iconUrl,
            iconLabel,
            isActive,
            isConfigDropdownOpen,
            onConfigDropdownOpenChange,
            isRenamePopupOpen,
            onRenamePopupOpenChange,
            handleClick,
            onOpenRenamePopup,
        } = useObjectTabTriggerState({
            dataConnection,
            object,
            onSelectObject,
        })

        return (
            <ManageDataTabDropdown
                objectSid={object._sid}
                open={isConfigDropdownOpen}
                onOpenChange={onConfigDropdownOpenChange}
                onOpenImportModal={onOpenImportModal}
                onOpenPermissionsModal={onOpenPermissionsModal}
                onOpenSettingsModal={onOpenSettingsModal}
                onOpenRenamePopup={onOpenRenamePopup}
                onPointerDown={stopPropagation}
            >
                <ManageDataTabRenamePopup
                    objectSid={object._sid}
                    onPointerDown={stopPropagation}
                    open={isRenamePopupOpen}
                    onOpenChange={onRenamePopupOpenChange}
                >
                    <TabsTrigger
                        ref={ref}
                        onClick={handleClick}
                        leftSlotContent={
                            <Box
                                as="img"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                }}
                                src={iconUrl}
                                alt={iconLabel}
                                noShrink
                            />
                        }
                        rightSlotContent={
                            <>
                                {isDirty && <Spinner size="2xs" noShrink />}
                                {isActive && !isDirty && (
                                    <Icon
                                        size="m"
                                        name={isConfigDropdownOpen ? 'ChevronUp' : 'ChevronDown'}
                                    />
                                )}
                            </>
                        }
                        {...props}
                    />
                </ManageDataTabRenamePopup>
            </ManageDataTabDropdown>
        )
    }
)

type OverflowTabTriggerProps = React.ComponentPropsWithoutRef<typeof DropdownItem> &
    ObjectTriggerProps

const OverflowTabTrigger: React.FC<OverflowTabTriggerProps> = React.memo(
    function OverflowTabTrigger({ object, dataConnection, onSelectObject, ...props }) {
        const { iconUrl, iconLabel } = useObjectTabTriggerState({
            dataConnection,
            object,
            onSelectObject,
        })

        return (
            <DropdownItem
                {...props}
                leftSlotContent={() => (
                    <Box
                        as="img"
                        style={{
                            width: '16px',
                            height: '16px',
                        }}
                        src={iconUrl}
                        alt={iconLabel}
                        noShrink
                    />
                )}
            />
        )
    }
)

const TAB_MIN_WIDTH = 140
const TAB_MAX_WIDTH = 226

type ObjectTabListProps = {
    isLoading?: boolean
    objects: ObjectDto[]
    dataConnections: DataConnectionDto[]
    isDirtyPanes?: Record<string, boolean>
    onSelectObject: (objectSid: string) => void
    onOpenImportModal: () => void
    onOpenPermissionsModal: () => void
    onOpenSettingsModal: () => void
    onOpenImportCsvModal: () => void
    onOpenImportTableDataModal: () => void
}

const ObjectTabList: React.FC<ObjectTabListProps> = React.memo(function ObjectTabList({
    isLoading,
    objects,
    dataConnections,
    isDirtyPanes,
    onSelectObject,
    onOpenImportModal,
    onOpenPermissionsModal,
    onOpenSettingsModal,
    onOpenImportCsvModal,
    onOpenImportTableDataModal,
}) {
    const dataConnectionsBySid = useMemo(() => {
        return dataConnections.reduce(
            (acc, dc) => {
                acc[dc._sid] = dc
                return acc
            },
            {} as Record<string, DataConnectionDto>
        )
    }, [dataConnections])

    if (isLoading) return null

    return (
        <TabsList
            width="full"
            overflow={true}
            autoOverflowMaxCount={true}
            overflowDropdownHead={<DropdownHeadSearch />}
            className={ManageDataTablesTabsListStyle}
            endAction={
                <ManageDataAddOrImportDropdown
                    onSelectObject={onSelectObject}
                    onOpenImportCsvModal={onOpenImportCsvModal}
                    onOpenImportTableDataModal={onOpenImportTableDataModal}
                />
            }
            renderOverflowDropdownItem={(_idx, child, props) => {
                const object = child.props.object
                const dataConnection = child.props.dataConnection

                return (
                    <OverflowTabTrigger
                        {...props}
                        dataConnection={dataConnection}
                        object={object}
                        isDirty={isDirtyPanes?.[object._sid]}
                        onSelectObject={onSelectObject}
                        onOpenImportModal={onOpenImportModal}
                        onOpenPermissionsModal={onOpenPermissionsModal}
                        onOpenSettingsModal={onOpenSettingsModal}
                    />
                )
            }}
        >
            {objects.map((object) => {
                return (
                    <ObjectTabTrigger
                        overflowMinWidth={TAB_MIN_WIDTH}
                        overflowMaxWidth={TAB_MAX_WIDTH}
                        label={object.name}
                        value={object._sid}
                        key={object._sid}
                        dataConnection={dataConnectionsBySid[object.data_connection ?? '']}
                        object={object}
                        isDirty={isDirtyPanes?.[object._sid]}
                        onSelectObject={onSelectObject}
                        onOpenImportModal={onOpenImportModal}
                        onOpenPermissionsModal={onOpenPermissionsModal}
                        onOpenSettingsModal={onOpenSettingsModal}
                    />
                )
            })}
        </TabsList>
    )
})

type ObjectTabContentProps = {
    isLoading?: boolean
    objects: ObjectDto[]
    selectedObjectSid: string
    updateDirtyPanes: (objectSid: string, dirty: boolean) => void
}

const ObjectTabContent: React.FC<ObjectTabContentProps> = React.memo(function ObjectTabContent({
    objects,
    isLoading,
    selectedObjectSid,
    updateDirtyPanes,
}) {
    if (isLoading) return null

    return (
        <>
            {objects.map((object) => {
                const isActive = selectedObjectSid === object._sid

                return (
                    <TabsContent key={object._sid} value={object._sid}>
                        <ManageDataGrid
                            objectSid={object._sid}
                            isActive={isActive}
                            updateDirtyPanes={updateDirtyPanes}
                        />
                    </TabsContent>
                )
            })}
        </>
    )
})

type LoadingScreenProps = React.ComponentPropsWithoutRef<typeof Box> & {
    isLoading: boolean
}

const LoadingScreen: React.FC<LoadingScreenProps> = React.memo(function LoadingScreen({
    isLoading,
    ...props
}) {
    if (!isLoading) return null

    return (
        <Box
            height="full"
            width="full"
            background="surface"
            flex
            center
            justifyContent="center"
            {...props}
        >
            <Spinner size="xl" />
        </Box>
    )
})
